<div class="restaurant-form-page">
  <header class="page-header">
    <div class="header-content">
      <a class="back-link" routerLink="/admin/restaurants">
        <i class="fas fa-arrow-left"></i>
        Retour aux restaurants
      </a>
      <h1>{{ isEditMode() ? 'Modifier le restaurant' : 'Nouveau restaurant' }}</h1>
    </div>
  </header>

  <form (ngSubmit)="saveRestaurant()" [formGroup]="restaurantForm" class="form-container">
    <!-- Informations générales -->
    <section class="form-section">
      <h2>
        <i class="fas fa-info-circle"></i>
        Informations générales
      </h2>

      <div class="form-grid">
        <div class="form-group full-width">
          <label for="name">Nom du restaurant *</label>
          <input formControlName="name" id="name" placeholder="Ex: Le Gourmet Parisien" type="text"/>
          @if (restaurantForm.get('name')?.invalid && restaurantForm.get('name')?.touched) {
            <span class="error-text">Le nom est requis (min. 2 caractères)</span>
          }
        </div>

        <div class="form-group full-width">
          <label for="description">Description</label>
          <textarea formControlName="description" id="description"
                    placeholder="Décrivez votre restaurant, sa cuisine, son ambiance..."
                    rows="4"></textarea>
        </div>

        <div class="form-group">
          <label for="cuisineType">Type de cuisine *</label>
          <select formControlName="cuisineType" id="cuisineType">
            <option value="">Sélectionner</option>
            <option value="french">Française</option>
            <option value="italian">Italienne</option>
            <option value="japanese">Japonaise</option>
            <option value="chinese">Chinoise</option>
            <option value="indian">Indienne</option>
            <option value="mexican">Mexicaine</option>
            <option value="american">Américaine</option>
            <option value="lebanese">Libanaise</option>
            <option value="thai">Thaïlandaise</option>
            <option value="other">Autre</option>
          </select>
        </div>

        <div class="form-group">
          <label for="priceRange">Gamme de prix</label>
          <select formControlName="priceRange" id="priceRange">
            <option value="$">$ - Économique</option>
            <option value="$$">$$ - Modéré</option>
            <option value="$$$">$$$ - Haut de gamme</option>
            <option value="$$$$">$$$$ - Luxe</option>
          </select>
        </div>

        <div class="form-group">
          <label for="phone">Téléphone *</label>
          <input formControlName="phone" id="phone" placeholder="+33 1 23 45 67 89" type="tel"/>
        </div>

        <div class="form-group">
          <label for="email">Email</label>
          <input formControlName="email" id="email" placeholder="contact@restaurant.com" type="email"/>
        </div>

        <div class="form-group">
          <label for="website">Site web</label>
          <input formControlName="website" id="website" placeholder="https://www.restaurant.com" type="url"/>
        </div>

        <div class="form-group">
          <label for="status">Statut</label>
          <select formControlName="status" id="status">
            <option value="pending">En attente</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="suspended">Suspendu</option>
          </select>
        </div>
      </div>
    </section>

    <!-- Adresse -->
    <section class="form-section">
      <h2>
        <i class="fas fa-map-marker-alt"></i>
        Adresse
      </h2>

      <div class="form-grid" formGroupName="address">
        <div class="form-group full-width">
          <label for="street">Rue et numéro *</label>
          <input formControlName="street" id="street" placeholder="123 Rue de la Paix" type="text"/>
        </div>

        <div class="form-group">
          <label for="city">Ville *</label>
          <input formControlName="city" id="city" placeholder="Paris" type="text"/>
        </div>

        <div class="form-group">
          <label for="postalCode">Code postal *</label>
          <input formControlName="postalCode" id="postalCode" placeholder="75001" type="text"/>
        </div>

        <div class="form-group">
          <label for="country">Pays</label>
          <select formControlName="country" id="country">
            <option value="FR">France</option>
            <option value="BE">Belgique</option>
            <option value="CH">Suisse</option>
            <option value="LU">Luxembourg</option>
          </select>
        </div>
      </div>
    </section>

    <!-- Images -->
    <section class="form-section">
      <h2>
        <i class="fas fa-images"></i>
        Images
      </h2>

      <div class="images-grid">
        <div class="image-upload main-image">
          <label>Image principale</label>
          <div (click)="triggerFileInput('mainImage')" class="upload-area">
            @if (mainImagePreview()) {
              <img [src]="mainImagePreview()" alt="Aperçu"/>
            } @else {
              <i class="fas fa-cloud-upload-alt"></i>
              <span>Cliquez ou glissez une image</span>
              <span class="hint">Recommandé: 800x600px</span>
            }
          </div>
          <input #mainImage (change)="onMainImageChange($event)" accept="image/*" hidden type="file"/>
        </div>

        <div class="image-upload logo">
          <label>Logo</label>
          <div (click)="triggerFileInput('logoImage')" class="upload-area logo-area">
            @if (logoPreview()) {
              <img [src]="logoPreview()" alt="Logo"/>
            } @else {
              <i class="fas fa-store"></i>
              <span>Logo</span>
            }
          </div>
          <input #logoImage (change)="onLogoChange($event)" accept="image/*" hidden type="file"/>
        </div>
      </div>
    </section>

    <!-- Horaires d'ouverture -->
    <section class="form-section">
      <h2>
        <i class="fas fa-clock"></i>
        Horaires d'ouverture
      </h2>

      <div class="opening-hours">
        @for (day of daysOfWeek; track day.key) {
          <div class="day-row">
            <div class="day-name">{{ day.label }}</div>
            <div [formGroupName]="'openingHours'" class="hours-inputs">
              <div [formGroupName]="day.key">
                <label class="closed-toggle">
                  <input formControlName="closed" type="checkbox"/>
                  <span>Fermé</span>
                </label>
                @if (!restaurantForm.get('openingHours.' + day.key + '.closed')?.value) {
                  <div class="time-inputs">
                    <input formControlName="open" type="time"/>
                    <span>-</span>
                    <input formControlName="close" type="time"/>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    </section>

    <!-- Options de livraison -->
    <section class="form-section">
      <h2>
        <i class="fas fa-truck"></i>
        Options de livraison
      </h2>

      <div class="form-grid" formGroupName="deliveryOptions">
        <div class="form-group">
          <label for="deliveryRadius">Rayon de livraison (km)</label>
          <input formControlName="deliveryRadius" id="deliveryRadius" min="0" step="0.5" type="number"/>
        </div>

        <div class="form-group">
          <label for="deliveryFee">Frais de livraison (€)</label>
          <input formControlName="deliveryFee" id="deliveryFee" min="0" step="0.5" type="number"/>
        </div>

        <div class="form-group">
          <label for="minOrderAmount">Commande minimum (€)</label>
          <input formControlName="minOrderAmount" id="minOrderAmount" min="0" step="0.5" type="number"/>
        </div>

        <div class="form-group">
          <label for="freeDeliveryThreshold">Livraison gratuite à partir de (€)</label>
          <input formControlName="freeDeliveryThreshold" id="freeDeliveryThreshold" min="0" step="0.5" type="number"/>
        </div>

        <div class="form-group">
          <label for="estimatedDeliveryTime">Temps de livraison estimé (min)</label>
          <input formControlName="estimatedDeliveryTime" id="estimatedDeliveryTime" min="0" type="number"/>
        </div>
      </div>

      <div class="delivery-options">
        <label class="option-checkbox">
          <input formControlName="deliveryEnabled" type="checkbox"/>
          <span>Livraison activée</span>
        </label>
        <label class="option-checkbox">
          <input formControlName="pickupEnabled" type="checkbox"/>
          <span>Click & Collect activé</span>
        </label>
        <label class="option-checkbox">
          <input formControlName="dineInEnabled" type="checkbox"/>
          <span>Sur place activé</span>
        </label>
      </div>
    </section>

    <!-- Propriétaire / Gérant -->
    <section class="form-section">
      <h2>
        <i class="fas fa-user-tie"></i>
        Propriétaire / Gérant
      </h2>

      <div class="form-grid" formGroupName="owner">
        <div class="form-group">
          <label for="ownerFirstName">Prénom</label>
          <input formControlName="firstName" id="ownerFirstName" placeholder="Jean" type="text"/>
        </div>

        <div class="form-group">
          <label for="ownerLastName">Nom</label>
          <input formControlName="lastName" id="ownerLastName" placeholder="Dupont" type="text"/>
        </div>

        <div class="form-group">
          <label for="ownerEmail">Email</label>
          <input formControlName="email" id="ownerEmail" placeholder="jean.dupont@email.com" type="email"/>
        </div>

        <div class="form-group">
          <label for="ownerPhone">Téléphone</label>
          <input formControlName="phone" id="ownerPhone" placeholder="+33 6 12 34 56 78" type="tel"/>
        </div>
      </div>
    </section>

    <!-- Actions -->
    <div class="form-actions">
      <a class="btn btn-secondary" routerLink="/admin/restaurants">
        Annuler
      </a>
      <button [disabled]="restaurantForm.invalid || store.saving()" class="btn btn-primary" type="submit">
        @if (store.saving()) {
          <span class="spinner-sm"></span>
        }
        {{ isEditMode() ? 'Mettre à jour' : 'Créer le restaurant' }}
      </button>
    </div>
  </form>
</div>

